import { useCallback, useEffect, useRef, useState } from 'react';
import { apiService, DeliveryRetryStatus } from '../services/api';
import { useAppStore } from '../store/appStore';

export function useDeliveryRetry(conversationId: string, enabled: boolean) {
  const businessId = useAppStore(s => s.currentBusiness?._id);
  const scope = `${businessId}:${conversationId}`;
  const current = useRef(scope);
  current.current = scope;
  const [saved, setSaved] = useState<{ scope: string; data: DeliveryRetryStatus | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const mutation = useRef(0);
  useEffect(() => {
    let alive = true, fetching = false;
    setSaved(null); setError(''); setBusy(false); mutation.current++;
    if (!enabled || !businessId) return;
    const load = async () => {
      if (fetching) return;
      const version = mutation.current;
      fetching = true;
      try {
        const result = await apiService.getConversationDeliveryRetry(conversationId);
        if (alive && current.current === scope && version === mutation.current && result.statusCode === 1000) setSaved({ scope, data: result.data });
      } catch { /* Loading retry status must not prevent reading or replying in the inbox. */ }
      finally { fetching = false; }
    };
    void load();
    const timer = setInterval(load, 60000);
    return () => { alive = false; clearInterval(timer); };
  }, [scope, enabled, businessId, conversationId]);

  const configure = useCallback(async (on: boolean) => {
    const version = ++mutation.current;
    setBusy(true); setError('');
    try {
      const result = await apiService.setConversationDeliveryRetry(conversationId, on);
      if (current.current !== scope || version !== mutation.current) return;
      if (result.statusCode !== 1000) throw new Error(result.errors?.[0]?.message || 'Could not update automatic retry.');
      setSaved({ scope, data: result.data });
    } catch (err: any) { if (current.current === scope && version === mutation.current) setError(err.message || 'Could not update automatic retry.'); }
    finally { if (current.current === scope && version === mutation.current) setBusy(false); }
  }, [conversationId, scope]);
  return { retry: enabled && saved?.scope === scope ? saved.data : null, busy, error, configure };
}
